/**
 * Utilities for normalizing time formats across the application
 * 
 * Database format: "HH:mm:ss" (e.g., "09:00:00")
 * UI format: "HH:mm" (e.g., "09:00")
 */

/**
 * Normalizes a time string to HH:mm:ss format for database storage
 * @param time - Time string in "HH:mm" or "HH:mm:ss" format
 * @returns Time string in "HH:mm:ss" format
 * @example
 * normalizeTimeForDB("09:00") // "09:00:00"
 * normalizeTimeForDB("09:00:00") // "09:00:00"
 */
export function normalizeTimeForDB(time: string): string {
  // Already in HH:mm:ss format
  if (time.includes(':00:00') || time.match(/^\d{2}:\d{2}:\d{2}$/)) {
    return time;
  }
  // Convert HH:mm to HH:mm:ss
  if (time.match(/^\d{2}:\d{2}$/)) {
    return `${time}:00`;
  }
  // Return as-is if format is unexpected (defensive)
  return time;
}

/**
 * Normalizes a time string from database format to UI format
 * @param time - Time string in "HH:mm:ss" format
 * @returns Time string in "HH:mm" format
 * @example
 * normalizeTimeForUI("09:00:00") // "09:00"
 * normalizeTimeForUI("09:00") // "09:00"
 */
export function normalizeTimeForUI(time: string): string {
  return time.substring(0, 5); // Always "HH:mm"
}

/**
 * Parses a time slot string "HH:mm - HH:mm" and returns start and end times
 * @param slot - Time slot string in "HH:mm - HH:mm" format
 * @returns Object with start and end time strings
 * @example
 * parseTimeSlot("09:00 - 10:00") // { start: "09:00", end: "10:00" }
 */
export function parseTimeSlot(slot: string): { start: string; end: string } {
  const parts = slot.split(' - ');
  return {
    start: parts[0]?.trim() || '',
    end: parts[1]?.trim() || ''
  };
}

/**
 * Parses a date string in YYYY-MM-DD format to a local Date object
 * Avoids timezone offset issues when parsing dates from the database
 * @param dateString - Date string in "YYYY-MM-DD" format
 * @returns Date object in local timezone
 * @example
 * parseDateLocal("2025-11-05") // Date object for November 5, 2025 in local time
 */
export function parseDateLocal(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // Month is 0-indexed in JS
}

/**
 * Formate un objet Date en chaîne "YYYY-MM-DD" en heure locale
 * Évite le décalage de fuseau horaire causé par toISOString()
 * @param date - Objet Date à formater
 * @returns Chaîne au format "YYYY-MM-DD"
 * @example
 * formatDateLocal(new Date(2025, 9, 29)) // "2025-10-29"
 */
export function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
